import { useState } from 'react';
import { consejoDirectivoData } from '@/mocks/nosotros-data';
import MemberModal from './MemberModal';

const memberImages: Record<string, string> = {
  'Julie Anna Freundt López': 'https://storage.helloreaddy.io/project_files/b36b426b-b2cb-46b2-8bd1-3f029dd7b0e4/e9d78437-def2-4d9a-9fa3-e31a87c94426_compressed_Julie-Freundt.webp',
  'Fernando Enrique Rentería Balado': 'https://readdy.ai/api/search-image?query=Professional%20portrait%20of%20peruvian%20male%20musician%20and%20performer%20in%20his%20mid%2040s%20warm%20natural%20expression%20artistic%20yet%20institutional%20atmosphere%20genuine%20character%20soft%20studio%20lighting%20authentic%20editorial%20portrait%20subtle%20charcoal%20and%20earth%20tones&width=600&height=750&seq=soniem-sec-ejecutivo-2026&orientation=portrait',
  'Óscar Vicente Cavero Aróstegui': 'https://readdy.ai/api/search-image?query=Authentic%20portrait%20of%20peruvian%20male%20musician%20and%20music%20producer%20in%20his%20late%2040s%20warm%20genuine%20expression%20artistic%20studio%20atmosphere%20natural%20lighting%20cultural%20character%20editorial%20quality%20portrait%20with%20subtle%20maroon%20tones&width=600&height=750&seq=soniem-sec-sociocultural-2026&orientation=portrait',
  'Ruth Karina Armas Ríos': 'https://readdy.ai/api/search-image?query=Elegant%20professional%20portrait%20of%20peruvian%20female%20singer%20and%20performer%20in%20her%20late%2030s%20warm%20natural%20expression%20artistic%20studio%20atmosphere%20genuine%20warm%20smile%20authentic%20editorial%20portrait%20photography%20soft%20lighting%20graceful%20presence&width=600&height=750&seq=soniem-vocal1-2026&orientation=portrait',
  'John Edgar Rojas Bernaza': 'https://readdy.ai/api/search-image?query=Authentic%20portrait%20of%20peruvian%20male%20musician%20and%20performer%20in%20his%20mid%2040s%20warm%20natural%20expression%20artistic%20studio%20setting%20genuine%20character%20soft%20lighting%20editorial%20portrait%20photography%20subtle%20earth%20tones%20professional%20atmosphere&width=600&height=750&seq=soniem-vocal2-2026&orientation=portrait',
};

export default function ConsejoDirectivoSection() {
  const [selectedMember, setSelectedMember] = useState<{
    name: string;
    role: string;
    bio: string;
    imageUrl: string;
    linkedinUrl: string;
  } | null>(null);

  const { title, subtitle, president, members } = consejoDirectivoData;

  const openModal = (name: string, role: string, bio: string, linkedinUrl?: string) => {
    setSelectedMember({
      name,
      role,
      bio,
      imageUrl: memberImages[name],
      linkedinUrl: linkedinUrl || '',
    });
  };

  return (
    <>
      <section className="py-16 md:py-24 bg-background-100">
        <div className="px-4 md:px-6 lg:px-10">
          <div className="max-w-5xl mx-auto">
            {/* Section Header */}
            <div className="text-center mb-12 md:mb-16">
              <span className="inline-block text-xs md:text-sm font-semibold uppercase tracking-widest text-primary-600 mb-3">
                Liderazgo institucional
              </span>
              <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-900 leading-tight mb-3">
                {title}
              </h2>
              <p className="text-foreground-500 text-sm md:text-base">
                {subtitle}
              </p>
            </div>

            {/* Presidenta — Central & Prominent */}
            <div className="flex justify-center mb-14 md:mb-16">
              <div className="w-full max-w-md">
                <div
                  className="group rounded-2xl bg-background-50 border border-primary-200/60 overflow-hidden cursor-pointer hover:border-primary-300/80 transition-all duration-300"
                  onClick={() => openModal(president.name, president.role, president.bio, (president as { linkedinUrl?: string }).linkedinUrl)}
                >
                  {/* Presidenta Photo */}
                  <div className="relative h-72 md:h-80 overflow-hidden">
                    <img
                      src={memberImages[president.name]}
                      alt={president.name}
                      className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-700"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
                    {/* Semblanza trigger overlay */}
                    <div className="absolute inset-0 flex items-end justify-center pb-6 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/90 text-foreground-900 text-xs font-semibold whitespace-nowrap">
                        <i className="ri-eye-line" />
                        Ver semblanza
                      </span>
                    </div>
                  </div>
                  {/* Presidenta Info */}
                  <div className="p-5 md:p-6 text-center">
                    <span className="inline-block px-3 py-1 rounded-full bg-primary-500 text-white text-xs font-semibold mb-3 whitespace-nowrap">
                      {president.role}
                    </span>
                    <h3 className="font-heading text-xl md:text-2xl font-bold text-foreground-900">
                      {president.name}
                    </h3>
                  </div>
                </div>
              </div>
            </div>

            {/* Other Members — 2x2 Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 md:gap-6">
              {members.map((member) => (
                <div
                  key={member.name}
                  className="group rounded-2xl bg-background-50 border border-secondary-200/60 overflow-hidden cursor-pointer hover:border-primary-200/60 transition-all duration-300"
                  onClick={() => openModal(member.name, member.role, member.bio, (member as { linkedinUrl?: string }).linkedinUrl)}
                >
                  {/* Member Photo */}
                  <div className="relative h-56 md:h-64 overflow-hidden">
                    <img
                      src={memberImages[member.name]}
                      alt={member.name}
                      className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-700"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent" />
                    <div className="absolute inset-0 flex items-end justify-center pb-5 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <span className="inline-flex items-center gap-2 px-3.5 py-2 rounded-full bg-white/90 text-foreground-900 text-xs font-semibold whitespace-nowrap">
                        <i className="ri-eye-line" />
                        Ver semblanza
                      </span>
                    </div>
                  </div>
                  {/* Member Info */}
                  <div className="p-4 md:p-5 text-center">
                    <span className="inline-block px-2.5 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-semibold mb-2 whitespace-nowrap">
                      {member.role}
                    </span>
                    <h4 className="font-heading text-base font-bold text-foreground-900">
                      {member.name}
                    </h4>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Modal */}
      <MemberModal
        isOpen={selectedMember !== null}
        onClose={() => setSelectedMember(null)}
        name={selectedMember?.name || ''}
        role={selectedMember?.role || ''}
        bio={selectedMember?.bio || ''}
        imageUrl={selectedMember?.imageUrl || ''}
        linkedinUrl={selectedMember?.linkedinUrl || ''}
      />
    </>
  );
}